import { useEffect, useState, useMemo, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
   Table, Tag, Input, Button, Modal, Form,
   DatePicker, message, Popconfirm, Space,
   Row, Col, Tooltip, Avatar
} from "antd";
import {
   EditOutlined, DeleteOutlined, CalendarOutlined,
   ClockCircleOutlined, WarningOutlined, PhoneOutlined,
   UserOutlined, SearchOutlined, CheckCircleOutlined,
   ExclamationCircleOutlined
} from "@ant-design/icons";
import dayjs from "dayjs";
import {
   getAllFollowups,
   getFollowupSummary,
   createFollowup,
   updateFollowup,
   deleteFollowup
} from "../../services/followupsApi";

const TODAY = dayjs().startOf("day");

const PRIORITY_CONFIG = {
   overdue: { color: "red", text: "Overdue", icon: <WarningOutlined /> },
   today: { color: "orange", text: "Today", icon: <ClockCircleOutlined /> },
   upcoming: { color: "green", text: "Upcoming", icon: <CalendarOutlined /> }
};

const styles = `
   .followups-card {
      background: #fff;
      border-radius: 10px;
      box-shadow: 0 1px 2px 0 rgba(0,0,0,0.03);
      border: 1px solid #f3f4f6;
   }
   .followups-table .ant-table-thead > tr > th {
      background: #f9fafb;
      font-size: 12px;
      padding: 10px 16px;
   }
   .followups-table .ant-table-tbody > tr > td {
      padding: 10px 16px;
      font-size: 13px;
   }
   .followups-table .ant-table-tbody > tr:hover > td {
      background: #f9fafb;
   }
   .stat-card {
      border-radius: 10px;
      transition: all 0.3s;
      cursor: pointer;
   }
   .stat-card:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(0,0,0,0.08);
   }
`;

// ─── Helper Functions ───
const getPriorityType = (date) => {
   const followupDate = dayjs(date);
   if (followupDate.isBefore(TODAY, "day")) return "overdue";
   if (followupDate.isSame(TODAY, "day")) return "today";
   return "upcoming";
};

const getPriorityConfig = (date) => PRIORITY_CONFIG[getPriorityType(date)];

// ─── Main Component ───
const FollowupsPage = () => {
   const navigate = useNavigate();
   const [searchParams] = useSearchParams();
   const type = searchParams.get("type");

   const [followups, setFollowups] = useState([]);
   const [searchText, setSearchText] = useState("");
   const [open, setOpen] = useState(false);
   const [editing, setEditing] = useState(null);
   const [loading, setLoading] = useState(false);
   const [stats, setStats] = useState({
      overdue: 0,
      today: 0,
      upcoming: 0
   });
   const [form] = Form.useForm();

   // ─── Fetch Followups ───
   const fetchFollowups = useCallback(async () => {
      setLoading(true);

      try {
         const [followupsRes, summaryRes] = await Promise.all([
            getAllFollowups(),
            getFollowupSummary()
         ]);

         setFollowups(followupsRes.data);
         setStats(summaryRes.data);

      } catch (error) {
         message.error("Failed to fetch follow-ups");
      } finally {
         setLoading(false);
      }
   }, []);

   useEffect(() => {
      fetchFollowups();
   }, [fetchFollowups]);

   // ─── Delete Handler ───
   const handleDelete = async (id) => {
      try {
         await deleteFollowup(id);
         message.success("Follow-up deleted");
         fetchFollowups();
      } catch (error) {
         message.error("Failed to delete");
      }
   };

   // ─── Submit Handler ───
   const handleSubmit = async (values) => {
      const payload = {
         ...values,
         next_followup_date: values.next_followup_date.format("YYYY-MM-DD")
      };

      try {
         if (editing) {
            await updateFollowup(editing.id, payload);
            message.success("Follow-up updated");
         } else {
            await createFollowup(payload);
            message.success("Follow-up added");
         }
         handleModalClose();
         fetchFollowups();
      } catch (error) {
         message.error("Operation failed");
      }
   };

   // ─── Edit Handler ───
   const handleEdit = (record) => {
      setEditing(record);
      form.setFieldsValue({
         lead_id: record.lead_id,
         note: record.note,
         next_followup_date: dayjs(record.next_followup_date),
         status: record.status
      });
      setOpen(true);
   };

   // ─── Modal Close ───
   const handleModalClose = () => {
      setOpen(false);
      setEditing(null);
      form.resetFields();
   };


   // ─── Filtered Data ───
   const filtered = useMemo(() => {
      return followups.filter(item => {
         if (type && getPriorityType(item.next_followup_date) !== type) {
            return false;
         }

         if (!searchText) return true;

         const searchLower = searchText.toLowerCase();
         return (
            item.lead_name?.toLowerCase().includes(searchLower) ||
            item.phone?.toString().includes(searchLower) ||
            item.note?.toLowerCase().includes(searchLower)
         );
      });
   }, [followups, type, searchText]);

   // ─── Table Columns ───
   const columns = [
      {
         title: "#",
         width: 50,
         align: "center",
         render: (_, __, index) => (
            <span className="text-xs text-gray-400">{index + 1}</span>
         )
      },
      {
         title: "Lead",
         dataIndex: "lead_name",
         render: (text, record) => (
            <div
               className="flex items-center gap-2 cursor-pointer hover:text-blue-600"
               onClick={() => navigate(`/leads/${record.lead_id}`)}
            >
               <Avatar size={28} style={{ backgroundColor: '#6366f1', fontSize: '12px' }}>
                  {text?.charAt(0)?.toUpperCase()}
               </Avatar>
               <div>
                  <span className="font-medium text-sm text-gray-800">{text}</span>
                  <div className="text-xs text-gray-400 flex items-center gap-1">
                     <PhoneOutlined style={{ fontSize: '10px' }} />
                     {record.phone || 'No phone'}
                  </div>
               </div>
            </div>
         )
      },
      {
         title: "Status",
         dataIndex: "status",
         width: 100,
         render: (status) => (
            <Tag
               color={status === "Done" ? "green" : "orange"}
               icon={status === "Done" ? <CheckCircleOutlined /> : <ClockCircleOutlined />}
            >
               {status}
            </Tag>
         )
      },
      {
         title: "Note",
         dataIndex: "note",
         ellipsis: true,
         render: (note) => (
            <Tooltip title={note}>
               <span className="text-sm text-gray-600">{note || '-'}</span>
            </Tooltip>
         )
      },
      {
         title: "Follow-up Date",
         dataIndex: "next_followup_date",
         width: 140,
         render: (date) => {
            const config = getPriorityConfig(date);
            return (
               <div className="flex items-center gap-1">
                  <span style={{ color: config.color, fontSize: '12px' }}>{config.icon}</span>
                  <span className="text-sm text-gray-700">
                     {date ? dayjs(date).format("DD MMM YYYY") : "-"}
                  </span>
               </div>
            );
         }
      },
      {
         title: "Priority",
         width: 100,
         render: (_, record) => {
            const config = getPriorityConfig(record.next_followup_date);
            return <Tag color={config.color} icon={config.icon}>{config.text}</Tag>;
         }
      },
      {
         title: "Created By",
         dataIndex: "created_by_name",
         width: 120,
         render: (name) => (
            <div className="flex items-center gap-1 text-gray-500 text-sm">
               <UserOutlined style={{ fontSize: '11px' }} />
               {name}
            </div>
         )
      },
      {
         title: "Actions",
         width: 100,
         align: "center",
         render: (_, record) => (
            <Space size="small">
               <Tooltip title="Edit">
                  <Button
                     type="text"
                     size="small"
                     icon={<EditOutlined />}
                     onClick={() => handleEdit(record)}
                  />
               </Tooltip>
               <Popconfirm
                  title="Delete this follow-up?"
                  onConfirm={() => handleDelete(record.id)}
                  okText="Yes"
                  cancelText="No"
                  okButtonProps={{ danger: true }}
               >
                  <Tooltip title="Delete">
                     <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                  </Tooltip>
               </Popconfirm>
            </Space>
         )
      }
   ];

   return (
      <div className="bg-gray-50 min-h-screen">
         <style>{styles}</style>

         {/* Header */}
         <div className="bg-white border-b px-6 py-4">
            <h1 className="text-xl font-semibold text-gray-800 m-0">Follow-ups</h1>
            <span className="text-gray-400 text-sm">
               {type ? `Showing ${type} follow-ups` : 'All follow-ups'} • {filtered.length} total
            </span>
         </div>

         <div className="p-6">
            {/* Stats Cards */}
            <Row gutter={[16, 16]} className="mb-4">
               <Col xs={24} sm={8}>
                  <div
                     className={`followups-card p-4 stat-card ${type === 'overdue' ? 'border-red-200 bg-red-50' : ''}`}
                     onClick={() => navigate('/followups?type=overdue')}
                  >
                     <div className="flex items-center justify-between">
                        <div>
                           <div className="flex items-center gap-2 text-red-600 mb-2">
                              <WarningOutlined />
                              <span className="text-sm font-medium">Overdue</span>
                           </div>
                           <div className="text-2xl font-bold text-gray-800">{stats.overdue}</div>
                        </div>
                        <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                           <ExclamationCircleOutlined className="text-red-500 text-lg" />
                        </div>
                     </div>
                  </div>
               </Col>

               <Col xs={24} sm={8}>
                  <div
                     className={`followups-card p-4 stat-card ${type === 'today' ? 'border-orange-200 bg-orange-50' : ''}`}
                     onClick={() => navigate('/followups?type=today')}
                  >
                     <div className="flex items-center justify-between">
                        <div>
                           <div className="flex items-center gap-2 text-orange-600 mb-2">
                              <ClockCircleOutlined />
                              <span className="text-sm font-medium">Today</span>
                           </div>
                           <div className="text-2xl font-bold text-gray-800">{stats.today}</div>
                        </div>
                        <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                           <CalendarOutlined className="text-orange-500 text-lg" />
                        </div>
                     </div>
                  </div>
               </Col>

               <Col xs={24} sm={8}>
                  <div
                     className={`followups-card p-4 stat-card ${type === 'upcoming' ? 'border-green-200 bg-green-50' : ''}`}
                     onClick={() => navigate('/followups?type=upcoming')}
                  >
                     <div className="flex items-center justify-between">
                        <div>
                           <div className="flex items-center gap-2 text-green-600 mb-2">
                              <CalendarOutlined />
                              <span className="text-sm font-medium">Upcoming</span>
                           </div>
                           <div className="text-2xl font-bold text-gray-800">{stats.upcoming}</div>
                        </div>
                        <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                           <CheckCircleOutlined className="text-green-500 text-lg" />
                        </div>
                     </div>
                  </div>
               </Col>
            </Row>

            {/* Table Card */}
            <div className="followups-card">
               <div className="p-4 border-b flex items-center justify-between">
                  <div className="flex items-center gap-2">
                     <CalendarOutlined className="text-gray-600" />
                     <span className="font-medium text-gray-700">Follow-ups List</span>
                     <Tag>{filtered.length}</Tag>
                  </div>
                  <Input.Search
                     placeholder="Search lead, phone, note..."
                     allowClear
                     style={{ width: 250 }}
                     onChange={(e) => setSearchText(e.target.value)}
                     prefix={<SearchOutlined className="text-gray-400" />}
                  />
               </div>

               <div className="followups-table">
                  <Table
                     size="small"
                     dataSource={filtered}
                     columns={columns}
                     rowKey="id"
                     loading={loading}
                     pagination={{
                        pageSize: 10,
                        showTotal: (total, range) => (
                           <span className="text-sm text-gray-500">
                              Showing <b>{range[0]}-{range[1]}</b> of <b>{total}</b>
                           </span>
                        ),
                        size: "small"
                     }}
                     scroll={{ x: 900 }}
                  />
               </div>
            </div>
         </div>

         {/* Modal */}
         <Modal
            title={editing ? "Edit Follow-up" : "Add Follow-up"}
            open={open}
            onCancel={handleModalClose}
            footer={null}
            width={400}
            destroyOnClose
         >
            <Form layout="vertical" form={form} className="mt-4" onFinish={handleSubmit}>
               <Form.Item
                  name="note"
                  label="Note"
                  rules={[{ required: true, message: "Please enter note" }]}
               >
                  <Input.TextArea rows={3} placeholder="Enter note" />
               </Form.Item>

               <Form.Item
                  name="next_followup_date"
                  label="Next Follow-up Date"
                  rules={[{ required: true, message: "Please select date" }]}
               >
                  <DatePicker style={{ width: "100%" }} />
               </Form.Item>

               <div className="flex justify-end gap-2">
                  <Button onClick={handleModalClose}>Cancel</Button>
                  <Button type="primary" htmlType="submit">
                     {editing ? "Update" : "Add"}
                  </Button>
               </div>
            </Form>
         </Modal>
      </div>
   );
};

export default FollowupsPage;